import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  fetchAlertsProfile,
  resetAlertsProfileState
} from '@/redux/alerts/alertsProfileSlice'
import { Table, Tag, Tooltip, DatePicker, Select, Button, Space, message, Tabs } from 'antd'
import dayjs from 'dayjs'
import { renderTextStr, formatDateTimeStr } from '@/utils/renderUtils'
import { ALERT_LEVEL_CONFIG } from '@/views/commandCenter/config'
import { getMonitoringAlarms } from '@/service/alerts'
import {
  getSeeData,
  getEventTimeMs,
  getEventTypeLabel,
  getStreamKey,
  normalizeImageUrls,
  sortSseItemsByTimeDesc,
  parseMonitoringStreamKey,
  getMonitoringAlertSummary
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'
import SimpleModal from '@/components/SimpleModal/Modal'

const { RangePicker } = DatePicker

const defaultPageSize = 20

/** Java 监控告警 level：LOW/MEDIUM/HIGH/CRITICAL 或 1–4 → 与 ALERT_LEVEL_CONFIG 同一套 */
const MONITORING_JAVA_LEVEL_MAP = {
  LOW: ALERT_LEVEL_CONFIG[1],
  MEDIUM: ALERT_LEVEL_CONFIG[2],
  HIGH: ALERT_LEVEL_CONFIG[3],
  CRITICAL: ALERT_LEVEL_CONFIG[4],
  1: ALERT_LEVEL_CONFIG[1],
  2: ALERT_LEVEL_CONFIG[2],
  3: ALERT_LEVEL_CONFIG[3],
  4: ALERT_LEVEL_CONFIG[4]
}

function pickMonitoringAlarmList(res) {
  const d = res?.data
  if (d == null) return []
  if (Array.isArray(d)) return d
  if (Array.isArray(d.data)) return d.data
  if (Array.isArray(d.list)) return d.list
  if (Array.isArray(d.records)) return d.records
  return []
}

function firstUrlInArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  const u = arr.find((x) => typeof x === 'string' && x.trim() !== '')
  return u ? u.trim() : ''
}

function getMessageMapFirstThumbnailUrl(record) {
  const m = record?.messageMap
  if (!m || typeof m !== 'object') return ''
  return firstUrlInArray(m.thumbnailUrls)
}

/** 弹窗大图：优先 messageMap.imageUrls */
function getMessageMapFirstFullImageUrl(record) {
  const m = record?.messageMap
  if (!m || typeof m !== 'object') return ''
  const fromFull = firstUrlInArray(m.imageUrls)
  if (fromFull) return fromFull
  return firstUrlInArray(m.thumbnailUrls)
}

function sseLocalMonitoringRowKey(record) {
  if (record.id != null) return record.id
  const see = getSeeData(record)
  const t = getEventTimeMs(see)
  const sk = getStreamKey(record)
  const msgTime = see?.messageTime ?? ''
  const createdAt = see?.createdAt ?? ''
  return `sse-local-${sk}-${t}-${msgTime}-${createdAt}`
}

function alertsProfileRowKey(record) {
  if (record.id != null && record.id !== '') return record.id
  if (record.alert_id != null && record.alert_id !== '') return record.alert_id
  return `alert-${record.create_time ?? ''}-${record.updated_at ?? ''}-${record.alert_type ?? ''}`
}

function monitoringJavaRowKey(record) {
  if (record.id != null && record.id !== '') return `java-alarm-${record.id}`
  return `java-alarm-${record.createdAt ?? ''}-${record.type ?? ''}-${record.deviceId ?? ''}`
}

function resolveMonitoringDeviceParams(props) {
  if (!props) return { deviceId: '', channelNo: '' }
  const { deviceId, channelNo, data = {}, monitoringStreamKey } = props
  let d = deviceId != null && deviceId !== '' ? String(deviceId).trim() : ''
  let c = channelNo != null && channelNo !== '' ? String(channelNo).trim() : ''
  if (data && typeof data === 'object') {
    if (!d) {
      d = data.deviceId != null ? String(data.deviceId).trim() : data.deviceSerial != null ? String(data.deviceSerial).trim() : ''
    }
    if (!c) {
      c = data.channelNo != null ? String(data.channelNo).trim() : ''
    }
  }
  if ((!d || !c) && monitoringStreamKey) {
    const parsed = parseMonitoringStreamKey(monitoringStreamKey)
    if (!d) d = parsed.deviceId
    if (!c) c = parsed.channelNo
  }
  if (c === '_') c = '1'
  if (d === '_unknown') d = ''
  return { deviceId: d, channelNo: c }
}

function getJavaAlarmMessageMapSummary(record) {
  const s = record?.messageMap?.summary
  return typeof s === 'string' && s.trim() !== '' ? s.trim() : ''
}

class NursingAlertsForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      data: {},
      monitoringRange: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')],
      monitoringIsProcessed: null,
      monitoringList: [],
      monitoringLoading: false
    }
    this.fetchedProfile = false
  }

  getMonitoringDeviceParams = () => resolveMonitoringDeviceParams(this.props)

  canQueryJavaMonitoring = () => {
    const { deviceId, channelNo } = this.getMonitoringDeviceParams()
    return Boolean(deviceId && channelNo)
  }

  fetchMonitoringAlarms = async () => {
    if (!this.canQueryJavaMonitoring()) return
    const { deviceId, channelNo } = this.getMonitoringDeviceParams()
    const { monitoringRange, monitoringIsProcessed } = this.state
    const range = Array.isArray(monitoringRange) && monitoringRange[0] && monitoringRange[1] ? monitoringRange : null
    if (!range) {
      message.warning('请选择起止时间')
      return
    }
    const start = range[0].valueOf()
    const end = range[1].valueOf()
    if (end <= start) {
      message.warning('结束时间须晚于开始时间')
      return
    }
    this.setState({ monitoringLoading: true })
    try {
      const res = await getMonitoringAlarms({
        deviceId,
        channelNo,
        startTime: start,
        endTime: end,
        isProcessed: monitoringIsProcessed
      })
      const list = pickMonitoringAlarmList(res)
      this.setState({ monitoringList: list, monitoringLoading: false })
    } catch (e) {
      this.setState({ monitoringList: [], monitoringLoading: false })
    }
  }

  formatJavaMonitoringLevel = (level) => {
    if (level == null || level === '') {
      return { text: '—', color: 'default' }
    }
    const raw =
      typeof level === 'object' && level != null && !Array.isArray(level)
        ? (level.value != null ? level.value : level.name)
        : level
    const k = raw != null && raw !== '' ? String(raw).trim() : ''
    return (
      (k && MONITORING_JAVA_LEVEL_MAP[k]) || {
        text: k ? renderTextStr(k) : '—',
        color: 'default'
      }
    )
  }

  componentDidMount() {
    const { dispatch, data, query = {}, serviceType, sseLocalItems } = this.props

    if (this.canQueryJavaMonitoring()) {
      this.fetchMonitoringAlarms()
    }

    if (Array.isArray(sseLocalItems) && sseLocalItems.length > 0) {
      return
    }

    if (serviceType === 'monitoring' && this.canQueryJavaMonitoring()) {
      return
    }

    this.fetchedProfile = true

    const finalQuery = {
      ...query,
      ...(data.member_id && { member_id: data.member_id }),
      ...(data.alert_id && { alert_id: data.alert_id }),
      ...(data.id && { id: data.id })
    }

    dispatch(
      fetchAlertsProfile({
        serviceType,
        query: finalQuery
      })
    )
  }

  componentDidUpdate(prevProps) {
    const a = resolveMonitoringDeviceParams(this.props)
    const b = resolveMonitoringDeviceParams(prevProps)
    if (a.deviceId !== b.deviceId || a.channelNo !== b.channelNo) {
      if (this.canQueryJavaMonitoring()) {
        this.fetchMonitoringAlarms()
      } else {
        this.setState({ monitoringList: [] })
      }
    }
  }

  componentWillUnmount() {
    const { dispatch, serviceType } = this.props
    if (dispatch && this.fetchedProfile) {
      dispatch(
        resetAlertsProfileState({
          serviceType
        })
      )
    }
  }

  formatAlertLevel = (level) => {
    const levelInfo = ALERT_LEVEL_CONFIG[level] || { text: '-', color: 'default' }
    return levelInfo
  }

  formatStatus = (status) => {
    const statusMap = {
      0: { text: '未处理', color: 'red' },
      1: { text: '已处理', color: 'green' },
      未处理: { text: '未处理', color: 'red' },
      跟进中: { text: '跟进中', color: 'orange' },
      已结束: { text: '已结束', color: 'green' }
    }
    const statusInfo = statusMap[status] || { text: renderTextStr(status), color: 'default' }
    return statusInfo
  }

  handlePaginationChange = (pageNumber, pageSize) => {
    const {
      dispatch,
      serviceType,
      alertsProfileObj: { [serviceType]: { query } = {} }
    } = this.props

    dispatch(
      fetchAlertsProfile({
        serviceType,
        query: {
          ...query,
          pageNumber,
          pageSize
        }
      })
    )
  }

  renderJavaMonitoringToolbar() {
    const { monitoringRange, monitoringIsProcessed, monitoringLoading } = this.state
    const { deviceId, channelNo } = this.getMonitoringDeviceParams()
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 8, color: '#666', fontSize: 12 }}>
          设备 {renderTextStr(deviceId)} / 通道 {renderTextStr(channelNo)}
        </div>
        <Space wrap size={[8, 8]} align="center">
          <span style={{ fontSize: 12 }}>时间</span>
          <RangePicker
            showTime
            allowClear={false}
            value={monitoringRange}
            onChange={(dates) => this.setState({ monitoringRange: dates || [] })}
            style={{ minWidth: 320 }}
          />
          <span style={{ fontSize: 12 }}>处理状态</span>
          <Select
            style={{ width: 120 }}
            value={monitoringIsProcessed === null || monitoringIsProcessed === undefined ? 'all' : monitoringIsProcessed ? 'yes' : 'no'}
            onChange={(v) => {
              const next = v === 'all' ? null : v === 'yes'
              this.setState({ monitoringIsProcessed: next })
            }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'yes', label: '已处理' },
              { value: 'no', label: '未处理' }
            ]}
          />
          <Button type="primary" loading={monitoringLoading} onClick={this.fetchMonitoringAlarms}>
            查询
          </Button>
        </Space>
      </div>
    )
  }

  renderJavaMonitoringTable() {
    const { monitoringList, monitoringLoading } = this.state

    const columns = [
      {
        title: '级别',
        dataIndex: 'level',
        key: 'level',
        width: '10%',
        render: (lv) => {
          const { text, color } = this.formatJavaMonitoringLevel(lv)
          return <Tag color={color}>{text}</Tag>
        }
      },
      {
        title: '缩略图',
        key: 'thumbnail',
        width: '12%',
        render: (_, record) => {
          const thumb = getMessageMapFirstThumbnailUrl(record)
          const full = getMessageMapFirstFullImageUrl(record)
          const preview = thumb || full
          if (!preview) return '—'
          return (
            <SimpleModal baseProps={{ url: full || preview }}>
              <img
                src={preview}
                alt=""
                style={{
                  maxWidth: 64,
                  maxHeight: 48,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  display: 'block',
                  borderRadius: 2,
                  verticalAlign: 'top'
                }}
              />
            </SimpleModal>
          )
        }
      },
      {
        title: '已处理',
        dataIndex: 'isProcessed',
        key: 'isProcessed',
        width: '10%',
        render: (v) => (v === true ? <Tag color="green">是</Tag> : v === false ? <Tag color="red">否</Tag> : '—')
      },
      {
        title: '处理人ID',
        dataIndex: 'processor',
        key: 'processor',
        width: '12%',
        render: (v) => renderTextStr(v)
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        width: '32%',
        ellipsis: true,
        render: (text) => {
          const displayText = renderTextStr(text)
          return (
            <Tooltip title={displayText} placement="topLeft">
              <span>{displayText}</span>
            </Tooltip>
          )
        }
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '24%',
        render: (t) => formatDateTimeStr(t)
      }
    ]

    return (
      <Table
        tableLayout="fixed"
        style={{ width: '100%' }}
        loading={monitoringLoading}
        columns={columns}
        dataSource={monitoringList}
        rowKey={monitoringJavaRowKey}
        pagination={
          monitoringList.length > defaultPageSize
            ? { pageSize: defaultPageSize, showTotal: (total) => `共 ${total} 条` }
            : false
        }
        size="small"
        expandable={{
          expandedRowRender: (record) => {
            const summaryText = getJavaAlarmMessageMapSummary(record)
            return (
              <div style={{ padding: '4px 0' }}>
                {summaryText ? (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ color: '#666', fontSize: 12, marginBottom: 6 }}>说明</div>
                    <div
                      style={{
                        fontSize: 13,
                        lineHeight: 1.65,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'rgba(0,0,0,0.85)'
                      }}
                    >
                      {renderTextStr(summaryText)}
                    </div>
                  </div>
                ) : null}
                <div style={{ color: '#999', fontSize: 12, marginBottom: 6 }}>原始数据</div>
                <pre
                  style={{
                    maxHeight: 200,
                    overflow: 'auto',
                    fontSize: 12,
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'rgba(0,0,0,0.65)',
                    background: 'rgba(0,0,0,0.02)',
                    padding: 8,
                    borderRadius: 4
                  }}
                >
                  {JSON.stringify(
                    {
                      messageMap: record?.messageMap,
                      deviceId: record?.deviceId,
                      channelNo: record?.channelNo
                    },
                    null,
                    2
                  )}
                </pre>
              </div>
            )
          },
          rowExpandable: () => true
        }}
      />
    )
  }

  renderSseLocalMonitoringTable() {
    const { sseLocalItems } = this.props
    const list = sortSseItemsByTimeDesc(sseLocalItems || [])

    const columns = [
      {
        title: '时间',
        key: 't',
        width: 108,
        render: (_, record) => formatDateTimeStr(getEventTimeMs(getSeeData(record)))
      },
      {
        title: '说明',
        key: 'summary',
        ellipsis: true,
        render: (_, record) => {
          const text = renderTextStr(getEventTypeLabel(getSeeData(record)))
          return (
            <Tooltip title={text} placement="topLeft">
              <span>{text}</span>
            </Tooltip>
          )
        }
      },
      {
        title: '截图',
        key: 'pic',
        width: 72,
        render: (_, record) => {
          const urls = normalizeImageUrls(getSeeData(record))
          const u = urls[0]
          if (!u) return '—'
          return (
            <SimpleModal baseProps={{ url: u }}>
              <a>查看</a>
            </SimpleModal>
          )
        }
      }
    ]

    const pagination =
      list.length > defaultPageSize
        ? {
            pageSize: defaultPageSize,
            showTotal: (total) => `共 ${total} 条（本路缓存）`
          }
        : false

    return (
      <Table
        columns={columns}
        dataSource={list}
        rowKey={sseLocalMonitoringRowKey}
        pagination={pagination}
        scroll={{ x: 480 }}
        size="small"
        expandable={{
          expandedRowRender: (record) => {
            const see = getSeeData(record)
            const summaryText = getMonitoringAlertSummary(see) || ''
            return (
              <div style={{ padding: '4px 0' }}>
                {summaryText ? (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ color: '#666', fontSize: 12, marginBottom: 6 }}>说明</div>
                    <div
                      style={{
                        fontSize: 13,
                        lineHeight: 1.65,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'rgba(0,0,0,0.85)'
                      }}
                    >
                      {renderTextStr(summaryText)}
                    </div>
                  </div>
                ) : null}
                <div style={{ color: '#999', fontSize: 12, marginBottom: 6 }}>原始数据</div>
                <pre
                  style={{
                    maxHeight: 240,
                    overflow: 'auto',
                    fontSize: 12,
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'rgba(0,0,0,0.65)',
                    background: 'rgba(0,0,0,0.02)',
                    padding: 8,
                    borderRadius: 4
                  }}
                >
                  {JSON.stringify(record?.data != null ? record.data : record, null, 2)}
                </pre>
              </div>
            )
          },
          rowExpandable: () => true
        }}
      />
    )
  }

  render() {
    const { serviceType, alertsProfileObj = {}, sseLocalItems } = this.props
    const hasSse = Array.isArray(sseLocalItems) && sseLocalItems.length > 0
    const canJava = this.canQueryJavaMonitoring()

    const monitoringTabItems = []
    if (canJava) {
      monitoringTabItems.push({
        key: 'history',
        label: '历史告警',
        children: (
          <div>
            {this.renderJavaMonitoringToolbar()}
            {this.renderJavaMonitoringTable()}
          </div>
        )
      })
    }
    if (hasSse) {
      monitoringTabItems.push({
        key: 'live',
        label: '本路实时（缓存）',
        children: <div>{this.renderSseLocalMonitoringTable()}</div>
      })
    }
    if (monitoringTabItems.length >= 2) {
      return (
        <Tabs
          size="small"
          defaultActiveKey="live"
          destroyInactiveTabPane
          items={monitoringTabItems}
        />
      )
    }
    if (monitoringTabItems.length === 1) {
      return monitoringTabItems[0].children
    }

    const { query, fetching } = alertsProfileObj

    const renderData =
      (serviceType && alertsProfileObj[serviceType]) ||
      alertsProfileObj.data || {
        list: [],
        total: 0,
        pageNumber: 1,
        pageSize: 20
      }

    const columns = [
      {
        title: '预警等级',
        dataIndex: 'alert_level',
        key: 'alert_level',
        width: '10%',
        render: (level) => {
          const levelInfo = this.formatAlertLevel(level)
          return <Tag color={levelInfo.color}>{levelInfo.text}</Tag>
        }
      },
      {
        title: '预警类型',
        dataIndex: 'alert_type',
        key: 'alert_type',
        width: '15%',
        render: (text) => renderTextStr(text)
      },
      {
        title: '负责人',
        key: 'handler',
        width: '10%',
        render: (record) => (
          <div>
            <div style={{ marginBottom: 4 }}>{renderTextStr(record.handler)}</div>
          </div>
        )
      },
      {
        title: '状态',
        dataIndex: 'follow_status',
        key: 'follow_status',
        width: '10%',
        render: (status) => {
          const statusInfo = this.formatStatus(status)
          return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
        }
      },
      {
        title: '备注',
        dataIndex: 'issue_description',
        key: 'issue_description',
        width: '25%',
        render: (text) => {
          const displayText = renderTextStr(text)
          return (
            <Tooltip title={displayText} placement="topLeft">
              <div
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  lineHeight: '1.5'
                }}
              >
                {displayText}
              </div>
            </Tooltip>
          )
        }
      },
      {
        title: '时间信息',
        key: 'time_info',
        width: '20%',
        render: (record) => {
          return (
            <div>
              {record.create_time && (
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: '#666' }}>创建:</span> {formatDateTimeStr(record.create_time)}
                </div>
              )}
              {record.updated_at && (
                <div>
                  <span style={{ color: '#666' }}>更新:</span> {formatDateTimeStr(record.updated_at)}
                </div>
              )}
            </div>
          )
        }
      }
    ]

    const finalPagination = {
      current: query?.pageNumber || renderData.pageNumber || 1,
      pageSize: query?.pageSize || renderData.pageSize || defaultPageSize,
      total: renderData.total || 0,
      showTotal: (total) => `共 ${total} 条数据`,
      onChange: this.handlePaginationChange
    }

    return (
      <Table
        loading={fetching}
        columns={columns}
        dataSource={renderData.list || []}
        rowKey={alertsProfileRowKey}
        pagination={renderData.total > defaultPageSize ? finalPagination : false}
        scroll={{ x: 752 }}
        size="small"
      />
    )
  }
}

const NursingAlertsFormWrapper = (props) => {
  const dispatch = useDispatch()
  const alertsProfileObj = useSelector((state) => state.alerts.alertsProfile || {})

  return <NursingAlertsForm {...props} dispatch={dispatch} alertsProfileObj={alertsProfileObj} />
}

export default NursingAlertsFormWrapper
